<template>
  <div class="marketplace-home-root" :class="{'dark-mode': darkMode}">

    <!-- Theme Toggle -->
    <button class="mp-theme-toggle" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'" style="font-size:18px;"></i>
    </button>
    <head>
      <meta charset="utf-8" />
      <title>FLOCKR | Livestock Marketplace</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Browse and buy livestock across Kenya" name="description" />
      <meta content="Flockr" name="author" />
      <link rel="shortcut icon" href="assets/images/favicon.svg">

      <!-- DataTables -->
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />

      <!-- Bootstrap Css -->
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <!-- Bootstrap select styling -->
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
      <!-- Icons Css -->
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <!-- App Css-->
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <!-- custom Css-->
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">
      <div>

        <!-- ========== HERO SEARCH ========== -->
        <section class="mp-hero">
          <div class="container">
            <div class="row justify-content-center">
              <div class="col-lg-8 text-center">
                <h1 class="mp-hero-title">Find Your Perfect Livestock</h1>
                <p class="mp-hero-subtitle">Browse thousands of verified listings across 47 counties in Kenya</p>

                <!-- Search Bar -->
                <div class="mp-search-box mt-4">
                  <div class="input-group">
                    <span class="input-group-text mp-search-icon"><i class="mdi mdi-magnify font-size-20"></i></span>
                    <input v-model="searchQuery" type="text" class="form-control mp-search-input"
                           placeholder="Search by breed, species, location..."
                           @keyup.enter="goToSearch">
                    <select v-model="searchCounty" class="form-select mp-search-select">
                      <option value="">All Counties</option>
                      <option v-for="c in counties" :key="c.id" :value="c.id">{{ c.county_name }}</option>
                    </select>
                    <button class="btn btn-warning text-dark fw-bold px-4" @click="goToSearch">
                      <i class="mdi mdi-magnify me-1"></i> Search
                    </button>
                  </div>
                </div>

                <!-- Quick Filters -->
                <div class="mt-3 d-flex justify-content-center flex-wrap" style="gap:8px;">
                  <span class="mp-quick-filter" @click="quickSearch('dairy cows')">Dairy Cows</span>
                  <span class="mp-quick-filter" @click="quickSearch('goats')">Goats</span>
                  <span class="mp-quick-filter" @click="quickSearch('dorper sheep')">Dorper Sheep</span>
                  <span class="mp-quick-filter" @click="quickSearch('kienyeji chicken')">Kienyeji</span>
                  <span class="mp-quick-filter" @click="quickSearch('camels')">Camels</span>
                  <span class="mp-quick-filter" @click="quickSearch('pigs')">Pigs</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== SPECIES CATEGORIES ========== -->
        <section class="py-4" style="background:var(--fm-section-a,#F7F5F2);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="fw-bold mb-0" style="color:#BFA164;">Browse by Species</h5>
              <a href="javascript:void(0)" @click="$router.push('/marketplace/search')" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">View All <i class="mdi mdi-arrow-right"></i></a>
            </div>
            <div v-if="loadingSpecies" class="text-center py-4">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>
            <div v-else-if="speciesCategories.length === 0" class="text-center py-4">
              <i class="mdi mdi-paw" style="font-size:48px;color:#BFA164;"></i>
              <p class="text-muted mt-2">No species categories available</p>
            </div>
            <div v-else class="row g-3">
              <div class="col-lg-2 col-md-3 col-4" v-for="(sp, si) in speciesCategories" :key="si">
                <div class="mp-species-card" @click="$router.push('/marketplace/category/' + sp.id)">
                  <div class="mp-species-icon" :style="'background:' + sp.bgColor">
                    <i :class="sp.icon" :style="'color:' + sp.iconColor + ';font-size:32px;'"></i>
                  </div>
                  <div class="mp-species-name">{{ sp.name }}</div>
                  <div class="mp-species-count">{{ sp.count }} listings</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== FEATURED LISTINGS ========== -->
        <section class="py-4" style="background:var(--fm-section-b,#FFFFFF);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-star text-warning me-1"></i> Featured Listings</h5>
                <small class="text-muted">Premium listings from verified sellers</small>
              </div>
              <a href="javascript:void(0)" @click="$router.push({path:'/marketplace/search', query:{featured:'1'}})" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See All <i class="mdi mdi-arrow-right"></i></a>
            </div>

            <!-- Loading -->
            <div v-if="loadingFeatured" class="text-center py-5">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
              <p class="mt-2 text-muted">Loading featured listings...</p>
            </div>

            <div v-else class="row g-3">
              <div class="col-lg-3 col-md-4 col-6" v-for="(item, index) in featuredListings" :key="'f-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span class="badge bg-warning bg-opacity-25 text-warning" style="font-size:10px;"><i class="mdi mdi-star"></i> Featured</span>
                      <span v-if="item.is_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;"><i class="mdi mdi-check-decagram"></i> Verified</span>
                    </div>
                    <button class="mp-save-btn" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                    </button>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div class="mp-listing-meta">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location }}</div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">KES {{ formatPrice(item.price) }}</div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                        <i class="mdi mdi-star text-warning" v-if="item.seller_rating"></i>
                        <span v-if="item.seller_rating">{{ item.seller_rating }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="featuredListings.length === 0 && !loadingFeatured" class="col-12">
                <div class="text-center py-5">
                  <i class="mdi mdi-star-outline" style="font-size:48px;color:#BFA164;"></i>
                  <p class="text-muted mt-2">No featured listings at the moment</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== RECENT / LATEST LISTINGS ========== -->
        <section class="py-4" style="background:var(--fm-section-a,#F7F5F2);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-clock-fast text-info me-1"></i> Just Listed</h5>
                <small class="text-muted">Newest arrivals on the marketplace</small>
              </div>
              <a href="javascript:void(0)" @click="$router.push({path:'/marketplace/search', query:{sort:'newest'}})" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See All <i class="mdi mdi-arrow-right"></i></a>
            </div>

            <div v-if="loadingRecent" class="text-center py-5">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else class="row g-3">
              <div class="col-lg-3 col-md-4 col-6" v-for="(item, index) in recentListings" :key="'r-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span v-if="item.is_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;"><i class="mdi mdi-check-decagram"></i> Verified</span>
                    </div>
                    <button class="mp-save-btn" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                    </button>
                    <div class="mp-listing-time-badge">
                      <i class="mdi mdi-clock-outline"></i> {{ timeAgo(item.created_at) }}
                    </div>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div class="mp-listing-meta">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location }}</div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">KES {{ formatPrice(item.price) }}</div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="recentListings.length === 0 && !loadingRecent" class="col-12">
                <div class="text-center py-5">
                  <i class="mdi mdi-package-variant" style="font-size:48px;color:#BFA164;"></i>
                  <p class="text-muted mt-2">No listings yet. Be the first to sell!</p>
                  <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-warning text-dark fw-bold"><i class="mdi mdi-plus me-1"></i> Create Listing</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== POPULAR / TRENDING ========== -->
        <section class="py-4" style="background:var(--fm-section-b,#FFFFFF);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-trending-up text-success me-1"></i> Trending Now</h5>
                <small class="text-muted">Most viewed listings this week</small>
              </div>
              <a href="javascript:void(0)" @click="$router.push({path:'/marketplace/search', query:{sort:'popular'}})" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See All <i class="mdi mdi-arrow-right"></i></a>
            </div>

            <div v-if="loadingTrending" class="text-center py-5">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else class="row g-3">
              <div class="col-lg-3 col-md-4 col-6" v-for="(item, index) in trendingListings" :key="'t-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span v-if="item.is_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;"><i class="mdi mdi-check-decagram"></i> Verified</span>
                    </div>
                    <button class="mp-save-btn" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                    </button>
                    <div class="mp-listing-views-badge">
                      <i class="mdi mdi-eye"></i> {{ item.view_count || 0 }}
                    </div>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div class="mp-listing-meta">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location }}</div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">KES {{ formatPrice(item.price) }}</div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                        <i class="mdi mdi-star text-warning" v-if="item.seller_rating"></i>
                        <span v-if="item.seller_rating">{{ item.seller_rating }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== BUY REQUESTS ========== -->
        <section class="py-4" style="background:var(--fm-section-a,#F7F5F2);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-bullhorn text-warning me-1"></i> Active Buy Requests</h5>
                <small class="text-muted">Buyers looking for livestock — can you supply?</small>
              </div>
              <div>
                <a href="javascript:void(0)" @click="$router.push('/marketplace/buy-requests/create')" class="btn btn-sm btn-outline-warning me-2"><i class="mdi mdi-plus"></i> Post Request</a>
                <a href="javascript:void(0)" @click="$router.push('/marketplace/buy-requests')" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">View All <i class="mdi mdi-arrow-right"></i></a>
              </div>
            </div>

            <div v-if="loadingBuyRequests" class="text-center py-4">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else class="row g-3">
              <div class="col-lg-4 col-md-6" v-for="(req, ri) in buyRequests" :key="'br-'+ri">
                <div class="mp-buyrequest-card" @click="$router.push('/marketplace/buy-requests/' + req.id)">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <span class="badge bg-warning bg-opacity-10 text-warning">{{ req.species_name || 'Livestock' }}</span>
                    <small class="text-muted">{{ timeAgo(req.created_at) }}</small>
                  </div>
                  <h6 class="fw-bold mb-1" style="color:var(--fm-h,#1A1A1A);font-size:15px;">{{ req.title }}</h6>
                  <p class="mb-2" style="font-size:13px;color:var(--fm-m,#555);line-height:1.5;">{{ truncate(req.description, 80) }}</p>
                  <div class="d-flex flex-wrap mb-2" style="gap:12px;font-size:12px;color:var(--fm-s,#6B6356);">
                    <span><i class="mdi mdi-numeric"></i> Qty: {{ req.quantity_needed }}</span>
                    <span><i class="mdi mdi-cash"></i> Budget: KES {{ formatPrice(req.budget_min) }} - {{ formatPrice(req.budget_max) }}</span>
                    <span><i class="mdi mdi-map-marker"></i> {{ req.county_name || 'Any location' }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center pt-2" style="border-top:1px solid var(--fm-br,#2A2520);">
                    <div class="d-flex align-items-center" style="gap:6px;">
                      <div class="mp-seller-avatar">{{ getInitials(req.buyer_name) }}</div>
                      <span style="font-size:13px;color:var(--fm-s,#777);">{{ req.buyer_name }}</span>
                    </div>
                    <span class="badge bg-info bg-opacity-10 text-info" style="font-size:11px;">{{ req.response_count || 0 }} responses</span>
                  </div>
                </div>
              </div>
              <div v-if="buyRequests.length === 0 && !loadingBuyRequests" class="col-12">
                <div class="text-center py-4">
                  <i class="mdi mdi-bullhorn-outline" style="font-size:48px;color:#BFA164;"></i>
                  <p class="text-muted mt-2">No active buy requests</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== MARKET PRICES SNAPSHOT ========== -->
        <section class="py-4" style="background:var(--fm-section-b,#FFFFFF);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-chart-line text-info me-1"></i> Market Price Snapshot</h5>
                <small class="text-muted">Average prices across Kenya this week</small>
              </div>
              <a href="javascript:void(0)" @click="$router.push('/market/prices')" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">Full Report <i class="mdi mdi-arrow-right"></i></a>
            </div>
            <div v-if="loadingPrices" class="text-center py-4">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>
            <div v-else-if="marketPrices.length === 0" class="text-center py-4">
              <i class="mdi mdi-chart-line" style="font-size:48px;color:#BFA164;"></i>
              <p class="text-muted mt-2">No market price data available</p>
            </div>
            <div v-else class="row g-3">
              <div class="col-lg-2 col-md-3 col-4" v-for="(mp, mi) in marketPrices" :key="'mp-'+mi">
                <div class="mp-price-card">
                  <div class="mb-1"><i :class="mp.icon" style="font-size:24px;color:#BFA164;"></i></div>
                  <div class="fw-bold" style="font-size:14px;color:var(--fm-h,#1A1A1A);font-weight:600;">{{ mp.name }}</div>
                  <div class="fw-bold" style="font-size:16px;color:#BFA164;">KES {{ formatPrice(mp.avgPrice) }}</div>
                  <div :style="'font-size:12px;color:' + (mp.change >= 0 ? '#34c38f' : '#f46a6a')">
                    <i :class="mp.change >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                    {{ Math.abs(mp.change) }}% this week
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== SELL CTA ========== -->
        <section class="py-5" style="background:linear-gradient(135deg,var(--fm-hero1,#1A1510),var(--fm-hero2,#2A2018));border-top:1px solid var(--fm-br,#E0D8D0);">
          <div class="container">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <h3 class="fw-bold" style="color:#BFA164;">Have Livestock to Sell?</h3>
                <p class="mb-0" style="color:var(--fm-m,#A09888);font-size:16px;">List your animals in minutes. Reach thousands of verified buyers. Get paid via M-Pesa with escrow protection.</p>
              </div>
              <div class="col-lg-4 text-lg-end mt-3 mt-lg-0">
                <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-warning text-dark fw-bold btn-lg"><i class="mdi mdi-plus me-1"></i> Create Listing</a>
                <a href="javascript:void(0)" @click="$router.push('/marketplace/buy-requests')" class="btn btn-outline-warning btn-lg ms-2"><i class="mdi mdi-bullhorn me-1"></i> Buy Requests</a>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
    <div class="rightbar-overlay"></div>
    </body>
  </div>
</template>

<script>
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "MarketplaceHome",

  data() {
    return {
      // Theme
      darkMode: localStorage.getItem('flockr_theme') === 'dark',
      themeObserver: null,

      // Search
      searchQuery: '',
      searchCounty: '',
      counties: [],

      // Listings
      featuredListings: [],
      recentListings: [],
      trendingListings: [],
      buyRequests: [],

      // Loading states
      loadingFeatured: false,
      loadingRecent: false,
      loadingTrending: false,
      loadingBuyRequests: false,

      // Species categories (loaded from API)
      speciesCategories: [],
      loadingSpecies: false,

      // Market prices (loaded from API)
      marketPrices: [],
      loadingPrices: false
    };
  },

  mounted() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(() => this.syncTheme());
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    this.initializePage();
  },

  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  methods: {
    syncTheme(){
      const t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || (this.darkMode ? 'dark' : 'light');
      this.darkMode = t === 'dark';
      window.__flockrSetTheme(this.darkMode ? 'dark' : 'light');

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      const wrapper = document.getElementById('layout-wrapper');
      if (wrapper) {
        wrapper.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        wrapper.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    toggleTheme(){
      this.darkMode = !this.darkMode;
      localStorage.setItem('flockr_theme', this.darkMode ? 'dark' : 'light');
      this.syncTheme();
    },
    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},

    async initializePage() {
      this.fetchCounties();
      this.fetchSpeciesCategories();
      this.fetchFeaturedListings();
      this.fetchRecentListings();
      this.fetchTrendingListings();
      this.fetchBuyRequests();
      this.fetchMarketPrices();
    },

    // ============ API CALLS ============

    async fetchCounties() {
      try {
        const response = await executeJson({ function: "getCounties", countryCode: "KE" }, flocker);
        if (response.data.success) {
          this.counties = response.data.data.counties || [];
        }
      } catch (e) {
        console.error('Error fetching counties:', e);
      }
    },

    async fetchSpeciesCategories() {
      this.loadingSpecies = true;
      try {
        const response = await executeJson({ function: "getAnimalSpecies" }, flocker);
        if (response.data.success) {
          const species = response.data.data.species || [];
          this.speciesCategories = species.map(sp => ({
            id: sp.id,
            name: sp.name,
            slug: (sp.name || '').toLowerCase().replace(/\s+/g, '-'),
            icon: this.getSpeciesIcon(sp.name),
            bgColor: this.getSpeciesBgColor(sp.name),
            iconColor: this.getSpeciesIconColor(sp.name),
            count: 0
          }));
          this.fetchSpeciesCounts();
        }
      } catch (e) {
        console.error('Error fetching species categories:', e);
      } finally {
        this.loadingSpecies = false;
      }
    },

    async fetchSpeciesCounts() {
      try {
        var r = await executeJson({ function: "searchListings", limit: 1000 }, flocker);
        if (r.data.success) {
          var listings = r.data.data.listings || [];
          var counts = {};
          listings.forEach(function(l) {
            var sid = String(l.species_id);
            counts[sid] = (counts[sid] || 0) + 1;
          });
          this.speciesCategories.forEach(function(sp) {
            sp.count = counts[String(sp.id)] || 0;
          });
        }
      } catch (e) { console.error(e); }
    },

    async fetchFeaturedListings() {
      this.loadingFeatured = true;
      try {
        const response = await executeJson({
          function: "searchListings",
          limit: 8,
          page: 1,
          sortBy: "newest"
        }, flocker);
        if (response.data.success) {
          this.featuredListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching featured listings:', e);
      } finally {
        this.loadingFeatured = false;
      }
    },

    async fetchRecentListings() {
      this.loadingRecent = true;
      try {
        const response = await executeJson({
          function: "searchListings",
          limit: 8,
          page: 1,
          sortBy: "newest"
        }, flocker);
        if (response.data.success) {
          this.recentListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching recent listings:', e);
      } finally {
        this.loadingRecent = false;
      }
    },

    async fetchTrendingListings() {
      this.loadingTrending = true;
      try {
        const response = await executeJson({
          function: "searchListings",
          limit: 8,
          page: 1,
          sortBy: "popular"
        }, flocker);
        if (response.data.success) {
          this.trendingListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching trending listings:', e);
      } finally {
        this.loadingTrending = false;
      }
    },

    async fetchBuyRequests() {
      this.loadingBuyRequests = true;
      try {
        const response = await executeJson({
          function: "getBuyRequests",
          status: "active",
          limit: 3,
          page: 1
        }, flocker);
        if (response.data.success) {
          this.buyRequests = response.data.data.buyRequests || [];
        }
      } catch (e) {
        console.error('Error fetching buy requests:', e);
      } finally {
        this.loadingBuyRequests = false;
      }
    },

    async fetchMarketPrices() {
      this.loadingPrices = true;
      try {
        const response = await executeJson({ function: "getMarketPrices", limit: 6 }, flocker);
        if (response.data.success) {
          const prices = response.data.data.prices || [];
          this.marketPrices = prices.map(p => ({
            name: p.species_name || p.breed_name || 'Unknown',
            icon: this.getSpeciesIcon(p.species_name),
            avgPrice: p.avg_price || 0,
            change: p.price_change_pct || 0
          }));
        }
      } catch (e) {
        console.error('Error fetching market prices:', e);
      } finally {
        this.loadingPrices = false;
      }
    },

    async toggleSave(item) {
      const userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      try {
        const response = await executeJson({
          function: "saveListing",
          userId: userId,
          listingId: item.id
        }, flocker);
        if (response.data.success) {
          item.is_saved = !item.is_saved;
        }
      } catch (e) {
        console.error('Error toggling save:', e);
      }
    },

    // ============ NAVIGATION ============

    goToSearch() {
      const query = {};
      if (this.searchQuery) query.keyword = this.searchQuery;
      if (this.searchCounty) query.county = this.searchCounty;
      this.$router.push({ path: '/marketplace/search', query });
    },

    quickSearch(term) {
      this.searchQuery = term;
      this.goToSearch();
    },

    // ============ HELPERS ============

    formatPrice(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-KE');
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    getSpeciesIcon(speciesName) {
      if (!speciesName) return 'mdi mdi-paw';
      const s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'mdi mdi-cow';
      if (s.includes('goat')) return 'mdi mdi-alpha-g-circle';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'mdi mdi-sheep';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'mdi mdi-bird';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'mdi mdi-pig';
      if (s.includes('camel')) return 'mdi mdi-alpha-c-circle';
      return 'mdi mdi-paw';
    },

    getSpeciesBgColor(speciesName) {
      if (!speciesName) return 'rgba(191,161,100,.12)';
      const s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'rgba(191,161,100,.12)';
      if (s.includes('goat')) return 'rgba(52,195,143,.12)';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'rgba(80,165,241,.12)';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'rgba(241,180,76,.12)';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'rgba(232,67,147,.12)';
      if (s.includes('camel')) return 'rgba(155,89,182,.12)';
      return 'rgba(191,161,100,.12)';
    },

    getSpeciesIconColor(speciesName) {
      if (!speciesName) return '#BFA164';
      const s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return '#BFA164';
      if (s.includes('goat')) return '#34c38f';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return '#50a5f1';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return '#f1b44c';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return '#e84393';
      if (s.includes('camel')) return '#9b59b6';
      return '#BFA164';
    },

    timeAgo(dateStr) {
      if (!dateStr) return '';
      const now = new Date();
      const date = new Date(dateStr);
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
      if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
      return Math.floor(diff / 604800) + 'w ago';
    },

    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    }
  }
};
</script>


<style>
:root{
  --fm-h:#F5F0E8;
  --fm-s:#A09888;
  --fm-m:#A09888;
  --fm-bg1:#141414;
  --fm-bg2:#0F0F0F;
  --fm-bg3:#1E1E1E;
  --fm-br:#2A2520;
  --fm-page:#0A0A0A;
  --fm-hero1:#1A1510;
  --fm-hero2:#2A2018;
  --fm-section-a:#0F0F0F;
  --fm-section-b:#141414;
}

html[data-theme="light"]{
  --fm-h:#1A1A1A;
  --fm-s:#777777;
  --fm-m:#555555;
  --fm-bg1:#FFFFFF;
  --fm-bg2:#F7F5F2;
  --fm-bg3:#F0EBE4;
  --fm-br:#E8E2DA;
  --fm-page:#F7F5F2;
  --fm-hero1:#FFFDF9;
  --fm-hero2:#F7F5F2;
  --fm-section-a:#F7F5F2;
  --fm-section-b:#FFFFFF;
}

html[data-theme="dark"]{
  --fm-page:#0A0A0A;
  --fm-hero1:#1A1510;
  --fm-hero2:#2A2018;
  --fm-section-a:#0F0F0F;
  --fm-section-b:#141414;
}

html[data-theme="light"] body,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .marketplace-home-root{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}

html[data-theme="dark"] body,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .marketplace-home-root{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}
</style>

<style scoped>
/* HERO */
.mp-hero {
  background: linear-gradient(135deg, var(--fm-hero1,#1A1510) 0%, var(--fm-hero2,#2A2018) 50%, var(--fm-hero1,#1A1510) 100%)!important;
  padding: 80px 0 50px;
  position: relative;
  overflow: hidden;
}
.mp-hero::before {
  content: '';
  position: absolute;
  top: -40%;
  right: -15%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(191,161,100,.06) 0%, transparent 70%);
  border-radius: 50%;
}
.mp-hero-title { font-size: 42px; font-weight: 800; color: var(--fm-h,#F5F0E8)!important; line-height: 1.1; }
.mp-hero-subtitle { font-size: 17px; color: var(--fm-s,#A09888)!important; margin-top: 8px; }

/* SEARCH */
.mp-search-box { max-width: 720px; margin: 0 auto; }
.mp-search-box .input-group { background: #FFFFFF!important; border: 1px solid #E8E2DA; border-radius: 12px; overflow: hidden; }
.mp-search-icon { background: #141414 !important; border: none !important; color: #6B6356 !important; }
.mp-search-input { background: transparent !important; border: none !important; color: var(--fm-h,#F5F0E8) !important; font-size: 15px; }
.mp-search-input::placeholder { color: #6B6356 !important; }
.mp-search-input:focus { box-shadow: none !important; }
.mp-search-select { background: #F7F5F2 !important; border: none !important; border-left: 1px solid #E0D8D0 !important; color: #555 !important; max-width: 180px; font-size: 14px; }
.mp-search-select:focus { box-shadow: none !important; }

.mp-quick-filter {
  display: inline-block;
  background: rgba(191,161,100,.08)!important;
  color: var(--fm-s,#A09888)!important;
  padding: 5px 14px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .2s;
}
.mp-quick-filter:hover { background: rgba(191,161,100,.18)!important; color: #BFA164!important; }

/* SPECIES CARDS */
.mp-species-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 14px;
  padding: 20px 12px;
  text-align: center;
  cursor: pointer;
  transition: all .3s;
}
.mp-species-card:hover { border-color: #BFA164; transform: translateY(-3px); }
.mp-species-icon { width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px; }
.mp-species-name { font-size: 14px; font-weight: 700; color: #1A1A1A!important; }
.mp-species-count { font-size: 12px; color: #6B6356!important; margin-top: 2px; }

/* LISTING CARDS */
.mp-listing-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 14px;
  overflow: hidden;
  transition: all .3s;
  cursor: pointer;
  height: 100%;
}
.mp-listing-card:hover { border-color: #BFA164; transform: translateY(-3px); }
.mp-listing-img {
  position: relative;
  height: 170px;
  background: linear-gradient(135deg, #F0EBE4, #E8E2DA);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.mp-listing-photo { width: 100%; height: 100%; object-fit: cover; }
.mp-listing-placeholder { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; }
.mp-listing-badges { position: absolute; top: 8px; left: 8px; display: flex; gap: 4px; }
.mp-save-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  background: rgba(0,0,0,.5)!important;
  border: none;
  border-radius: 50%;
  color: #fff!important;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all .2s;
}
.mp-save-btn:hover { background: rgba(0,0,0,.7)!important; }
.mp-listing-time-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0,0,0,.6)!important;
  color: #ccc!important;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
}
.mp-listing-views-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0,0,0,.6)!important;
  color: #ccc!important;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
}
.mp-listing-body { padding: 14px; }
.mp-listing-title { font-size: 14px; font-weight: 700; color: #1A1A1A!important; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mp-listing-meta { display: flex; gap: 10px; font-size: 11px; color: #888!important; margin-bottom: 4px; flex-wrap: wrap; }
.mp-listing-location { font-size: 12px; color: #777!important; margin-bottom: 10px; }
.mp-listing-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 10px; border-top: 1px solid #2A2520; }
.mp-listing-price { font-size: 16px; font-weight: 800; color: #BFA164!important; }
.mp-listing-seller { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #888!important; }
.mp-seller-avatar {
  width: 20px;
  height: 20px;
  background: #BFA164!important;
  color: #0A0A0A!important;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* BUY REQUEST CARDS */
.mp-buyrequest-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 14px;
  padding: 20px;
  cursor: pointer;
  transition: all .3s;
  height: 100%;
}
.mp-buyrequest-card:hover { border-color: #BFA164; transform: translateY(-3px); }

/* PRICE CARDS */
.mp-price-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all .3s;
}
.mp-price-card:hover { border-color: #BFA164; }

/* RESPONSIVE */
@media (max-width: 991px) {
  .mp-hero-title { font-size: 32px; }
  .mp-search-select { display: none !important; }
}
@media (max-width: 575px) {
  .mp-hero { padding: 60px 0 30px; }
  .mp-hero-title { font-size: 26px; }
  .mp-listing-img { height: 130px; }
}
/* Theme Toggle */
.mp-theme-toggle {
  position:fixed; top:12px; right:16px; z-index:1050;
  width:40px; height:40px; border-radius:50%;
  background:#FFFFFF; border:1px solid #E0D8D0;
  color:#BFA164; cursor:pointer; transition:all .3s;
  display:flex; align-items:center; justify-content:center;
  box-shadow:0 2px 8px rgba(0,0,0,.1);
}
.mp-theme-toggle:hover { transform:scale(1.1); box-shadow:0 4px 12px rgba(191,161,100,.3); }
.dark-mode .mp-theme-toggle { background:#1E1E1E; border-color:#2A2520; color:#BFA164; box-shadow:0 2px 8px rgba(0,0,0,.4); }

/* ===== DARK MODE OVERRIDES ===== */
.dark-mode { color:#F5F0E8; }
.dark-mode section[style*="background:#F7F5F2"] { background:#0F0F0F!important; }
.dark-mode section[style*="background:#FFFFFF"] { background:#0A0A0A!important; }

/* Cards */
.dark-mode .mp-listing-card,
.dark-mode .mp-species-card,
.dark-mode .mp-buyrequest-card,
.dark-mode .mp-price-card { background:#141414!important; border-color:#2A2520!important; }
.dark-mode .mp-listing-card:hover,
.dark-mode .mp-species-card:hover,
.dark-mode .mp-buyrequest-card:hover,
.dark-mode .mp-price-card:hover { border-color:#BFA164!important; box-shadow:0 4px 12px rgba(191,161,100,.15)!important; }

/* Text */
.dark-mode .mp-listing-title { color:#F5F0E8!important; }
.dark-mode .mp-species-name { color:#F5F0E8!important; }
.dark-mode .mp-listing-meta { color:#A09888!important; }
.dark-mode .mp-listing-seller { color:#A09888!important; }
.dark-mode .mp-listing-location { color:#A09888!important; }
.dark-mode h5[style*="color:#BFA164"] { color:#BFA164!important; }

/* Buy requests inside dark cards */
.dark-mode .mp-buyrequest-card h6 { color:#F5F0E8!important; }
.dark-mode .mp-buyrequest-card p { color:#A09888!important; }
.dark-mode .mp-buyrequest-card span { color:#6B6356!important; }

/* Market price */
.dark-mode .mp-price-card div[style*="color:#1A1A1A"] { color:#F5F0E8!important; }

/* Search box */
.dark-mode .mp-search-box .input-group { background:#141414!important; border-color:#2A2520!important; }
.dark-mode .mp-search-input { background:transparent!important; color:#F5F0E8!important; }
.dark-mode .mp-search-select { background:#1A1A1A!important; border-color:#2A2520!important; color:#A09888!important; }

/* Listing image placeholder */
.dark-mode .mp-listing-img { background:linear-gradient(135deg,#1A1510,#0F0F0F)!important; }

/* Seller dot */
.dark-mode .mp-seller-dot { background:#1E1E1E!important; }

/* Borders */
.dark-mode .mp-buyrequest-card > div { border-color:#1E1E1E!important; }

/* Species icon colors in dark */
.dark-mode .mp-species-count { color:#6B6356!important; }

</style>
